import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';

// ============================================
// TYPE DEFINITIONS
// ============================================

export type BusinessType = 'sole_proprietor' | 'partnership' | 'llc' | 'corporation' | 'other';
export type ContactMethod = 'email' | 'phone';
export type AccountType = 'checking' | 'savings';
export type DocumentType = 'business_license' | 'tax_document' | 'banking_doc';

export interface UploadedFile {
  id: string;
  name: string;
  type: DocumentType;
  url: string;
  uploadedAt: string;
  size: number;
  originalFile?: File; // Actual File object for submission
  fieldName?: string; // Backend field name for submission
}


export interface OnboardingFormData {
  // Step 1: Business Information
  businessName: string;
  llcName: string;
  businessType: BusinessType | '';
  taxId: string;
  businessLicense: string;
  industry?: string;

  // Step 2: Contact Information
  contactName: string;
  contactEmail: string;
  contactPhone: string;
  preferredContact: ContactMethod;

  // Step 3: Address
  addressLine1: string;
  addressLine2?: string;
  city: string;
  state: string;
  zip: string;
  country: string;

  // Step 4: Banking & Documents
  bankName: string;
  accountType: AccountType | '';
  accountNumber: string;
  routingNumber: string;
  documentIds: string[];

  // Step 5: Agreement
  agreeToTerms: boolean;
  signatureData: string;
}

export interface OnboardingState {
  // Current step
  currentStep: number;

  // Form data
  formData: OnboardingFormData;

  // File management
  uploadedFiles: UploadedFile[];
  uploadProgress: Map<string, number>;

  // UI state
  errors: Map<string, string>;
  isSubmitting: boolean;
  successMessage?: string;
  applicationId?: string;

  // Actions
  nextStep: () => void;
  previousStep: () => void;
  goToStep: (step: number) => void;
  updateFormData: (fields: Partial<OnboardingFormData>) => void;
  updateFormField: (field: keyof OnboardingFormData, value: any) => void;
  addFile: (file: UploadedFile) => void;
  removeFile: (fileId: string) => void;
  setUploadProgress: (fileId: string, progress: number) => void;
  removeUploadProgress: (fileId: string) => void;
  setError: (field: string, message: string) => void;
  clearError: (field: string) => void;
  clearErrors: () => void;
  setSubmitting: (isSubmitting: boolean) => void;
  setSuccess: (message: string, applicationId: string) => void;
  resetForm: () => void;
  resetToStep: (step: number) => void;
}

// ============================================
// INITIAL STATE
// ============================================

const initialFormData: OnboardingFormData = {
  businessName: '',
  llcName: '',
  businessType: '',
  taxId: '',
  businessLicense: '',
  industry: '',
  contactName: '',
  contactEmail: '',
  contactPhone: '',
  preferredContact: 'email',
  addressLine1: '',
  addressLine2: '',
  city: '',
  state: '',
  zip: '',
  country: 'USA',
  bankName: '',
  accountType: '',
  accountNumber: '',
  routingNumber: '',
  documentIds: [],
  agreeToTerms: false,
  signatureData: '',
};

// ============================================
// ZUSTAND STORE
// ============================================

export const useOnboardingStore = create<OnboardingState>()(
  devtools(
    persist(
      (set, get) => ({
        // Initial state
        currentStep: 1,
        formData: initialFormData,
        uploadedFiles: [],
        uploadProgress: new Map(),
        errors: new Map(),
        isSubmitting: false,

        // Navigation actions
        nextStep: () =>
          set((state) => ({
            currentStep: Math.min(state.currentStep + 1, 5),
          })),

        previousStep: () =>
          set((state) => ({
            currentStep: Math.max(state.currentStep - 1, 1),
          })),

        goToStep: (step: number) =>
          set(() => ({
            currentStep: Math.max(1, Math.min(step, 5)),
          })),

        // Form data actions
        updateFormData: (fields: Partial<OnboardingFormData>) =>
          set((state) => ({
            formData: {
              ...state.formData,
              ...fields,
            },
          })),

        updateFormField: (field: keyof OnboardingFormData, value: any) =>
          set((state) => ({
            formData: {
              ...state.formData,
              [field]: value,
            },
          })),

        // File management actions
        addFile: (file: UploadedFile) =>
          set((state) => ({
            uploadedFiles: [...state.uploadedFiles, file],
            formData: {
              ...state.formData,
              documentIds: [...state.formData.documentIds, file.id],
            },
          })),

        removeFile: (fileId: string) =>
          set((state) => ({
            uploadedFiles: state.uploadedFiles.filter((f) => f.id !== fileId),
            formData: {
              ...state.formData,
              documentIds: state.formData.documentIds.filter((id) => id !== fileId),
            },
          })),

        setUploadProgress: (fileId: string, progress: number) =>
          set((state) => {
            const newProgress = new Map(state.uploadProgress);
            newProgress.set(fileId, progress);
            return { uploadProgress: newProgress };
          }),

        removeUploadProgress: (fileId: string) =>
          set((state) => {
            const newProgress = new Map(state.uploadProgress);
            newProgress.delete(fileId);
            return { uploadProgress: newProgress };
          }),

        // Error handling actions
        setError: (field: string, message: string) =>
          set((state) => {
            const newErrors = new Map(state.errors);
            newErrors.set(field, message);
            return { errors: newErrors };
          }),

        clearError: (field: string) =>
          set((state) => {
            const newErrors = new Map(state.errors);
            newErrors.delete(field);
            return { errors: newErrors };
          }),

        clearErrors: () =>
          set(() => ({
            errors: new Map(),
          })),

        // Submission state
        setSubmitting: (isSubmitting: boolean) =>
          set(() => ({
            isSubmitting,
          })),

        setSuccess: (message: string, applicationId: string) =>
          set(() => ({
            successMessage: message,
            applicationId,
            isSubmitting: false,
          })),

        // Form reset actions
        resetForm: () =>
          set(() => ({
            currentStep: 1,
            formData: initialFormData,
            uploadedFiles: [],
            uploadProgress: new Map(),
            errors: new Map(),
            isSubmitting: false,
            successMessage: undefined,
            applicationId: undefined,
          })),

        resetToStep: (step: number) =>
          set(() => ({
            currentStep: Math.max(1, Math.min(step, 5)),
            errors: new Map(),
          })),
      }),
      {
        name: 'onboarding-store',
        partialize: (state) => ({
          currentStep: state.currentStep,
          formData: state.formData,
          // Note: uploadedFiles are NOT persisted because they contain File objects
          // which cannot be serialized to localStorage
        }),
      }
    )
  )
);
